// src/client/components/settings/DataManagementTab.tsx
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Separator } from '@client/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@client/components/ui/alert-dialog';
import { Database, Download, RefreshCw, AlertTriangle } from 'lucide-react';
import { FormField } from '@client/components/shared/FormField';
import { ApiClient } from '@client/lib/api-client';
import { useToast } from '@client/hooks/use-toast';

interface DataManagementTabProps {
  loadSettings: () => Promise<void>;
}

const POPULATE_OPTIONS = [
  { value: 'populateDefaultSelectors', label: 'Populate Default Selectors' },
  { value: 'populateDefaultScores', label: 'Populate Default Score Ranges' },
  { value: 'populateDefaultUsers', label: 'Populate Default Users' },
  { value: 'populateDefaultsAll', label: 'Populate All Defaults' },
  { value: 'populateDemo', label: 'Populate Demo Data (Profiles & Lists)' },
];

export default function DataManagementTab({ loadSettings }: DataManagementTabProps) {
  const { toast } = useToast();
  const [selectedFunction, setSelectedFunction] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);

  const handlePopulate = async () => {
    if (!selectedFunction) {
      toast({
        title: 'No Function Selected',
        description: 'Please select a populate function first.',
        variant: 'destructive',
      });
      return;
    }

    setIsExecuting(true);
    try {
      await ApiClient.post(`/admin/populate/${selectedFunction}`);
      await loadSettings();
      toast({
        title: 'Data Initialized Successfully',
        description: `${selectedFunction} completed successfully.`,
      });
      setSelectedFunction('');
    } catch (error: any) {
      toast({
        title: 'Initialize Failed',
        description: `Failed to initialize ${selectedFunction}: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleExportData = async () => {
    try {
      // TODO: Implement export functionality
      toast({
        title: 'Export Started',
        description: 'Data export is being prepared...',
      });
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description: `Failed to export data: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleImportData = async () => {
    try {
      // TODO: Implement import functionality
      toast({
        title: 'Import Started',
        description: 'Data import is being processed...',
      });
    } catch (error: any) {
      toast({
        title: 'Import Failed',
        description: `Failed to import data: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleResetAllData = async () => {
    try {
      // TODO: Implement reset all data functionality
      toast({
        title: 'Reset Started',
        description: 'All data is being reset...',
      });
    } catch (error: any) {
      toast({
        title: 'Reset Failed',
        description: `Failed to reset data: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-4 w-4" />
          System Management
        </CardTitle>
        <CardDescription>Manage your application data and initialize demo content</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Populate Functions */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Initialize Data</h3>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <FormField
                id="populate-function"
                label="Initialize Function"
                type="select"
                value={selectedFunction}
                onChange={(value) => setSelectedFunction(String(value))}
                options={POPULATE_OPTIONS.map(opt => ({
                  value: opt.value,
                  label: opt.label
                }))}
                placeholder="Select populate function"
              />
            </div>
            <Button 
              onClick={handlePopulate}
              disabled={!selectedFunction || isExecuting}
            >
              <Database className="h-4 w-4 mr-2" />
              {isExecuting ? 'Executing...' : 'Execute'}
            </Button>
          </div>
          {selectedFunction && (
            <p className="text-sm text-muted-foreground">
              {POPULATE_OPTIONS.find(opt => opt.value === selectedFunction)?.label}
            </p>
          )}
        </div>
        
        <Separator />
        
        {/* Import/Export Functions */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Data Operations</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" onClick={handleExportData}>
              <Download className="h-4 w-4 mr-2" />
              Export Data
            </Button>
            <Button variant="outline" onClick={handleImportData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Import Data
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Reset All Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete all your data including profiles, lists, and settings.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleResetAllData}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    Delete All Data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}